/** Session handoff from KB library/editor → Dev tab rescope tool (localStorage). */

export const KB_DEV_RESCOPE_STORAGE_KEY = "kb_dev_rescope_intent_v1";

export type KbDevRescopeIntentV1 = {
  v: 1;
  slug: string;
  title: string;
  bodyMarkdown: string;
  current: {
    scope: string;
    organization_id: string | null;
    visibility: string | null;
    status: string;
    audience?: string;
    doc_type?: string | null;
    version?: number | null;
  };
  openedAt: string;
};

export function writeKbDevRescopeIntent(payload: Omit<KbDevRescopeIntentV1, "v" | "openedAt">): void {
  try {
    const full: KbDevRescopeIntentV1 = {
      v: 1,
      ...payload,
      openedAt: new Date().toISOString(),
    };
    localStorage.setItem(KB_DEV_RESCOPE_STORAGE_KEY, JSON.stringify(full));
  } catch {
    /* ignore quota / private mode */
  }
}

export function readKbDevRescopeIntent(): KbDevRescopeIntentV1 | null {
  try {
    const raw = localStorage.getItem(KB_DEV_RESCOPE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as KbDevRescopeIntentV1;
    if (parsed?.v !== 1 || typeof parsed.slug !== "string" || !parsed.slug.trim()) return null;
    if (typeof parsed.bodyMarkdown !== "string") parsed.bodyMarkdown = "";
    if (!parsed.current || typeof parsed.current !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearKbDevRescopeIntent(): void {
  try {
    localStorage.removeItem(KB_DEV_RESCOPE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function describePermissionsSummary(args: {
  scope: string;
  visibility: string | null | undefined;
  orgLabel: string | null;
  status?: string;
}): string {
  const { scope, visibility, orgLabel, status } = args;
  const vis =
    visibility === "org"
      ? "visibility org — end-user retrieval limited by organization membership (and published status)."
      : "visibility public — shared/manufacturer or app library rules apply.";
  const st = status ? ` Status: ${status}.` : "";
  if (scope === "org") {
    return `Org-scoped document${orgLabel ? ` for ${orgLabel}` : ""}. ${vis}${st}`;
  }
  if (scope === "manufacturer") {
    return `Manufacturer-scoped document${orgLabel ? ` (tenant overlay: ${orgLabel})` : " (global manufacturer library)"}. ${vis}${st}`;
  }
  if (scope === "app") {
    return `App-scoped document${orgLabel ? ` (tenant overlay: ${orgLabel})` : " (global app library)"}. ${vis}${st}`;
  }
  return `Scope ${scope}. ${vis}${st}`;
}
